import { History, RotateCcw, X } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import type { TranslationRevision } from '@/lib/api';

interface Props {
  open: boolean;
  loading: boolean;
  revisions: TranslationRevision[];
  canRevert: boolean;
  onRevert: (value: string) => void;
  onClose: () => void;
}

export function HistoryDrawer({ open, loading, revisions, canRevert, onRevert, onClose }: Props) {
  if (!open) return null;
  return (
    <aside
      role="dialog"
      aria-label="Revision history"
      className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-surface shadow-2xl"
    >
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="inline-flex items-center gap-2 text-sm font-semibold">
          <History className="size-4 text-primary" aria-hidden />
          Revision history
        </h3>
        <button
          type="button"
          aria-label="Close history"
          onClick={onClose}
          className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="size-4" aria-hidden />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {loading ? (
          <Spinner label="Loading revisions…" />
        ) : revisions.length === 0 ? (
          <p className="text-xs text-muted-foreground">No edits recorded for this language yet.</p>
        ) : (
          <ol className="space-y-3">
            {revisions.map((rev) => (
              <li key={rev.id} className="rounded-md border border-border bg-card p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {new Date(rev.createdAt).toLocaleString()} · {rev.status}
                      {rev.changedBy ? ` · ${rev.changedBy}` : ''}
                      {rev.source ? ` · ${rev.source}` : ''}
                    </p>
                    <p className="break-words text-sm text-foreground">
                      {rev.value || <span className="italic text-muted-foreground">(empty)</span>}
                    </p>
                    {rev.previousValue !== undefined && rev.previousValue !== rev.value && (
                      <p className="text-[11px] text-muted-foreground line-through">
                        {rev.previousValue}
                      </p>
                    )}
                  </div>
                  {canRevert && (
                    <button
                      type="button"
                      onClick={() => onRevert(rev.value)}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-[11px] font-medium hover:bg-muted"
                    >
                      <RotateCcw className="size-3" aria-hidden />
                      Revert
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </aside>
  );
}
